/**
 * Refill appointment — the browser half of the card 6827 wire contract.
 *
 * The purchase calendar (`CalendarDemo`) speaks a DISPLAY shape:
 *   { date: "September 3, 2026", time: "9:00 AM", timezone: "Eastern (EST)", utcTime: "…Z" }
 * `POST /orders/:id/refill/submit` speaks a WIRE shape (validation.js `requireRefillAppointment`):
 *   { utcTime, timezone, formatted }
 *
 * These two pure functions are the only place that translation happens. They live here rather
 * than inside the modal because the modal cannot be unit-tested on this box — jest runs
 * `testEnvironment: 'node'` with no jsdom — and the exact bytes on the wire are precisely what a
 * browser click cannot assert.
 *
 * ⛔ `timezone` is passed through VERBATIM and is never converted to an IANA zone. The calendar's
 * value is a human label ("Eastern (EST)"); `Intl.DateTimeFormat({ timeZone })` throws on it and
 * takes the React tree down with it. The server schema is `Joi.string().max(100)` — a free string —
 * so the label is already valid and no conversion is needed or wanted.
 *
 * ⛔ Neither function decides WHETHER scheduling is required. That is `refill.schedulingRequired`
 * from the calculate response, authored once on the server; a second client-side predicate is the
 * exact drift card 6827 was written to prevent.
 */

const MAX_FORMATTED_LENGTH = 200;

/**
 * The human-readable slot string sent as `formatted` and rendered in the checkout summary.
 * Returns "" when either half is missing, so a partial slot can never render as "undefined at ...".
 */
export const formatAppointmentDisplay = (appointment) => {
  const date = appointment?.date?.trim() ?? '';
  const time = appointment?.time?.trim() ?? '';
  if (!date || !time) return '';
  return `${date} at ${time}`.slice(0, MAX_FORMATTED_LENGTH);
};

/**
 * Display shape → wire shape. Returns null when there is no usable slot, which is what keeps the
 * DoctorNow/unknown request body byte-identical to the one the live frontend sends today: the
 * caller spreads the result only when it is non-null.
 *
 * `utcTime` is the one field with no fallback — without it there is nothing to book, so an
 * appointment lacking it is not an appointment.
 */
export const toRefillAppointmentPayload = (appointment) => {
  const utcTime = appointment?.utcTime ?? null;
  if (!utcTime) return null;
  return {
    utcTime,
    timezone: appointment?.timezone ?? '',
    formatted: formatAppointmentDisplay(appointment)
  };
};

export default { formatAppointmentDisplay, toRefillAppointmentPayload };
